/**
 * Clerk Appearance Configuration
 * Applies MerchMidas brand colors (dark gold/black theme) to all Clerk UI surfaces:
 *   - UserButton popover dropdown
 *   - Account modal (Profile, Security, Billing tabs)
 *   - Sign-in / Sign-up pages
 *
 * Brand palette:
 *   #050505  → near-black background
 *   #0f0e0c  → zeus card/panel background
 *   #ffd700  → gold primary
 *   #af8300  → pirate gold secondary / muted text
 *   #755b00  → yukon gold borders
 *   #f2f2f2  → near-white foreground text
 */

export const clerkAppearance = {
  /* ─── Global Design Tokens ─────────────────────────────────────────── */
  variables: {
    colorPrimary: "#ffd700",
    colorBackground: "#050505",
    colorInputBackground: "#0f0e0c",
    colorInputText: "#f2f2f2",
    colorText: "#f2f2f2",
    colorTextOnPrimaryBackground: "#050505",
    colorTextSecondary: "#af8300",
    colorDanger: "#ef4444",
    colorSuccess: "#22c55e",
    colorNeutral: "#755b00",
    borderRadius: "0.5rem",
    fontFamily: "inherit",
    fontSize: "14px",
    spacingUnit: "1rem",
  },

  /* ─── Component-Level Overrides (Tailwind class strings) ───────────── */
  elements: {
    /* ── Root card/modal shell ── */
    card: "bg-[#0f0e0c] border border-[#755b00] shadow-2xl shadow-[#ffd700]/10",
    cardBox: "bg-[#0f0e0c] shadow-2xl shadow-[#ffd700]/10",

    /* ── Modal header ── */
    headerTitle: "text-[#ffd700] font-bold",
    headerSubtitle: "text-[#af8300]",

    /* ── Left sidebar nav (Account modal) ── */
    navbar: "bg-[#050505] border-r border-[#755b00]",
    navbarMobileMenuRow: "bg-[#050505] border-b border-[#755b00]",
    navbarButton: "text-[#af8300] hover:text-[#ffd700] hover:bg-[#0f0e0c] rounded-md",
    navbarButtonIcon: "text-[#af8300]",

    /* ── Page/section title rows ── */
    profileSectionTitle: "border-b border-[#755b00]",
    profileSectionTitleText: "text-[#f2f2f2] font-semibold",
    profileSectionContent: "text-[#f2f2f2]",

    /* ── Row actions (e.g. "Update profile" link) ── */
    profileSectionPrimaryButton:
      "text-[#ffd700] hover:text-[#ffd700]/80 border border-[#755b00] hover:border-[#ffd700] rounded-md px-3 py-1 text-sm",
    profileSectionItem: "border-b border-[#755b00]/50",

    /* ── Form fields ── */
    formField: "",
    formFieldLabel: "text-[#af8300] text-sm font-medium",
    formFieldInput:
      "bg-[#050505] border border-[#755b00] text-[#f2f2f2] placeholder:text-[#755b00] focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700] rounded-md",
    formFieldInputShowPasswordButton: "text-[#af8300] hover:text-[#ffd700]",
    formFieldErrorText: "text-red-400",
    formFieldSuccessText: "text-green-400",
    formFieldHintText: "text-[#af8300]",

    /* ── Buttons ── */
    formButtonPrimary:
      "bg-[#ffd700] text-[#050505] hover:bg-[#af8300] font-semibold transition-colors",
    formButtonReset: "text-[#af8300] hover:text-[#ffd700]",
    formResendCodeLink: "text-[#ffd700] hover:text-[#ffd700]/80",

    /* ── Social / OAuth buttons ── */
    socialButtonsBlockButton:
      "bg-[#050505] border border-[#755b00] text-[#f2f2f2] hover:bg-[#0f0e0c] hover:border-[#ffd700] transition-colors",
    socialButtonsBlockButtonText: "text-[#f2f2f2]",
    socialButtonsBlockButtonArrow: "text-[#af8300]",

    /* ── Divider ── */
    dividerLine: "bg-[#755b00]",
    dividerText: "text-[#af8300]",

    /* ── Badges (e.g. "Active", "Primary") ── */
    badge:
      "bg-[#ffd700]/15 text-[#ffd700] border border-[#755b00] text-xs font-medium",

    /* ── Avatar ring ── */
    avatarBox: "ring-2 ring-[#755b00] hover:ring-[#ffd700]",

    /* ── UserButton popover dropdown ── */
    userButtonPopoverCard:
      "bg-[#0f0e0c] border border-[#755b00] shadow-xl shadow-[#ffd700]/10 rounded-xl",
    userButtonPopoverActions: "bg-[#0f0e0c] rounded-xl",
    userButtonPopoverActionButton:
      "text-[#f2f2f2] hover:bg-[#050505] hover:text-[#ffd700] rounded-lg transition-colors",
    userButtonPopoverActionButtonIcon: "text-[#af8300]",
    userButtonPopoverActionButtonText: "text-[#f2f2f2]",
    userButtonPopoverFooter:
      "bg-[#050505] border-t border-[#755b00] rounded-b-xl",
    userButtonPopoverFooterPagesLink: "text-[#ffd700] hover:text-[#ffd700]/80",

    /* ── User preview row (name + email in popover) ── */
    userPreview: "bg-[#0f0e0c]",
    userPreviewMainIdentifier: "text-[#ffd700] font-semibold",
    userPreviewSecondaryIdentifier: "text-[#af8300]",

    /* ── Footer ("Secured by Clerk") ── */
    footer: "bg-[#050505] border-t border-[#755b00]",
    footerAction: "text-[#af8300]",
    footerActionText: "text-[#af8300]",
    footerActionLink: "text-[#ffd700] hover:text-[#ffd700]/80",
    footerPages: "bg-[#050505]",

    /* ── Internal links ── */
    identityPreviewEditButton: "text-[#ffd700] hover:text-[#ffd700]/80",

    /* ── Active device / sessions list ── */
    activeDeviceListItem: "bg-[#0f0e0c] border border-[#755b00] rounded-lg",

    /* ── Alert banners ── */
    alertText: "text-[#f2f2f2]",
    alert: "border border-[#755b00] bg-[#0f0e0c]",

    /* ── OTP/code input ── */
    otpCodeFieldInput:
      "bg-[#050505] border border-[#755b00] text-[#ffd700] font-bold text-center focus:border-[#ffd700]",

    /* ── Table (active sessions) ── */
    tableHead: "text-[#af8300]",
    tableRow: "border-b border-[#755b00]/40",

    /* ── Subscription / plan cards (Billing tab) ── */
    subscriptionPlanItem: "bg-[#0f0e0c] border border-[#755b00] rounded-xl",

    /* ── Back link / breadcrumb ── */
    breadcrumbsItem: "text-[#af8300]",
    breadcrumbsItemDivider: "text-[#755b00]",

    /* ── Page body backgrounds ── */
    pageScrollBox: "bg-[#050505]",
    rootBox: "bg-transparent",
    main: "bg-[#050505]",
  },
};
